import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DEFAULT_SALON_PUBLIC_THEME } from '../../../theme/salonPublicTheme';


export const resolveSalonThemeTargetIds = ({
  scope,
  currentSalonId,
  targets = [],
  selectedSalonIds = [],
}) => {
  const currentId = Number(currentSalonId);
  const eligibleIds = new Set(
    targets.map(({ salon_id: salonId }) => Number(salonId)),
  );
  eligibleIds.add(currentId);

  let candidateIds;
  if (scope === 'all') {
    candidateIds = [...eligibleIds];
  } else if (scope === 'selected') {
    candidateIds = selectedSalonIds
      .map(Number)
      .filter((salonId) => eligibleIds.has(salonId));
  } else {
    candidateIds = [currentId];
  }

  return [...new Set([currentId, ...candidateIds])]
    .sort((left, right) => left - right);
};

export const isSalonThemeApplyScopeEligible = ({
  targets = [],
  currentSalonId,
}) => {
  const currentId = Number(currentSalonId);
  return targets.length >= 2 && targets.some(
    ({ salon_id: salonId }) => Number(salonId) === currentId,
  );
};

const SalonThemeApplyScope = ({
  scope,
  onScopeChange,
  targets = [],
  currentSalonId,
  selectedSalonIds = [],
  onSelectedSalonIdsChange,
  disabled = false,
  error = '',
  accentColor = DEFAULT_SALON_PUBLIC_THEME.primary_color,
}) => {
  const { t } = useTranslation();
  const currentId = Number(currentSalonId);

  if (!isSalonThemeApplyScopeEligible({ targets, currentSalonId })) {
    return null;
  }

  const handleTargetChange = (targetId, checked) => {
    const normalizedTargetId = Number(targetId);
    const nextIds = checked
      ? [...selectedSalonIds, normalizedTargetId]
      : selectedSalonIds.filter(
        (salonId) => Number(salonId) !== normalizedTargetId,
      );
    onSelectedSalonIdsChange(
      [...new Set([currentId, ...nextIds.map(Number)])]
        .sort((left, right) => left - right),
    );
  };

  return (
    <FormControl component="fieldset" error={Boolean(error)} disabled={disabled}>
      <FormLabel component="legend" id="salon-theme-apply-scope-label">
        {t('salon_theme.apply_scope.legend')}
      </FormLabel>
      <RadioGroup
        aria-labelledby="salon-theme-apply-scope-label"
        data-testid="salon-theme-scope-segments"
        value={scope}
        onChange={(event) => onScopeChange(event.target.value)}
        row
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          mt: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          '& .MuiFormControlLabel-root': {
            m: 0,
            px: 1,
            minHeight: 44,
            borderInlineEnd: { xs: 0, sm: 1 },
            borderBottom: { xs: 1, sm: 0 },
            borderColor: 'divider',
          },
          '& .MuiFormControlLabel-root:last-of-type': {
            borderInlineEnd: 0,
            borderBottom: 0,
          },
        }}
      >
        <FormControlLabel
          value="current"
          control={<Radio sx={{ '&.Mui-checked': { color: accentColor } }} />}
          label={t('salon_theme.apply_scope.current')}
          style={scope === 'current' ? { borderColor: accentColor } : undefined}
          sx={{ bgcolor: scope === 'current' ? `${accentColor}14` : 'transparent' }}
        />
        <FormControlLabel
          value="selected"
          control={<Radio sx={{ '&.Mui-checked': { color: accentColor } }} />}
          label={t('salon_theme.apply_scope.selected')}
          style={scope === 'selected' ? { borderColor: accentColor } : undefined}
          sx={{ bgcolor: scope === 'selected' ? `${accentColor}14` : 'transparent' }}
        />
        <FormControlLabel
          value="all"
          control={<Radio sx={{ '&.Mui-checked': { color: accentColor } }} />}
          label={t('salon_theme.apply_scope.all')}
          style={scope === 'all' ? { borderColor: accentColor } : undefined}
          sx={{ bgcolor: scope === 'all' ? `${accentColor}14` : 'transparent' }}
        />
      </RadioGroup>

      {scope === 'selected' && (
        <Box sx={{ pt: 1.25 }}>
          <Stack
            data-testid="salon-theme-target-chips"
            direction="row"
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            {targets.map((target) => {
              const targetId = Number(target.salon_id);
              const isCurrent = targetId === currentId;
              const isSelected = isCurrent || selectedSalonIds.some(
                (salonId) => Number(salonId) === targetId,
              );
              return (
                <FormControlLabel
                  key={targetId}
                  control={(
                    <Checkbox
                      checked={isSelected}
                      onChange={(event) => handleTargetChange(
                        targetId,
                        event.target.checked,
                      )}
                      disabled={disabled || isCurrent}
                      sx={{ '&.Mui-checked': { color: accentColor } }}
                    />
                  )}
                  label={target.name}
                  style={isSelected ? { borderColor: accentColor } : undefined}
                  sx={{
                    m: 0,
                    pr: 1.25,
                    border: 1,
                    borderColor: isSelected ? accentColor : 'divider',
                    borderRadius: 999,
                    bgcolor: isSelected ? `${accentColor}14` : 'background.paper',
                  }}
                />
              );
            })}
          </Stack>
          <FormHelperText>
            {t('salon_theme.apply_scope.selected_help')}
          </FormHelperText>
        </Box>
      )}

      {error && <FormHelperText role="alert">{error}</FormHelperText>}
    </FormControl>
  );
};

export default SalonThemeApplyScope;
